import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const TitleSection = styled('div', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 内容部分样式
const ContentSection = styled('section', {
  marginBottom: '3rem'
});

// 部分标题样式
const SectionTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '$textPrimary'
});

// 部分描述样式
const SectionDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  color: '$textSecondary',
  lineHeight: 1.6,
  maxWidth: '800px'
});

// 奖励卡片网格容器样式
const RewardsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '2rem',
  marginBottom: '3rem'
});

// 奖励卡片样式
const RewardCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
  }
});

// 奖励卡片头部样式
const RewardCardHeader = styled('div', {
  padding: '2rem',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  color: 'white',
  textAlign: 'center'
});

// 奖励卡片标题样式
const RewardCardTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem'
});

// 奖励卡片副标题样式
const RewardCardSubtitle = styled('p', {
  fontSize: '1rem',
  opacity: 0.9
});

// 奖励卡片内容样式
const RewardCardContent = styled('div', {
  padding: '1.5rem'
});

// 奖励卡片描述样式
const RewardCardDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  marginBottom: '1.5rem',
  lineHeight: 1.6
});

// 奖励列表样式
const RewardList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '0',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem'
});

// 奖励列表项样式
const RewardListItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  fontSize: '0.9rem',
  lineHeight: 1.5
});

// 奖励列表项图标样式
const RewardListItemIcon = styled('span', {
  color: '$accentPrimary',
  fontWeight: 'bold',
  fontSize: '1.25rem'
});

// 层级表格容器样式
const TierTableContainer = styled('div', {
  overflowX: 'auto',
  marginBottom: '3rem'
});

// 层级表格样式
const TierTable = styled('table', {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid $borderColor',
  borderRadius: '8px',
  overflow: 'hidden'
});

// 表格头部样式
const TableHead = styled('thead', {
  background: '$backgroundHighlight'
});

// 表格行样式
const TableRow = styled('tr', {
  '&:not(:last-child)': {
    borderBottom: '1px solid $borderColor'
  },
  '&:nth-child(even)': {
    background: '$backgroundSecondary'
  }
});

// 表格头部单元格样式
const TableHeaderCell = styled('th', {
  padding: '1rem',
  textAlign: 'left',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 表格单元格样式
const TableCell = styled('td', {
  padding: '1rem',
  color: '$textSecondary'
});

// 特殊表格单元格样式
const SpecialTableCell = styled('td', {
  padding: '1rem',
  color: '$accentPrimary',
  fontWeight: 'bold'
});

// 图文区块样式
const ImageTextBlock = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '3rem',
  marginBottom: '3rem',
  
  '@media (max-width: 968px)': {
    flexDirection: 'column',
    gap: '2rem'
  },
  
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
        
        '@media (max-width: 968px)': {
          flexDirection: 'column'
        }
      }
    }
  }
});

// 图片容器样式
const ImageContainer = styled('div', {
  flex: '1',
  height: '400px',
  borderRadius: '12px',
  overflow: 'hidden',
  
  '@media (max-width: 968px)': {
    width: '100%',
    height: '300px'
  }
});

// 图片样式
const Image = styled('div', {
  width: '100%',
  height: '100%',
  background: 'center/cover no-repeat'
});

// 文本内容样式
const TextContent = styled('div', {
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 文本内容标题样式
const TextTitle = styled('h3', {
  fontSize: '1.75rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 文本内容段落样式
const TextParagraph = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 特殊事件容器样式
const EventsContainer = styled('div', {
  marginTop: '2rem'
});

// 事件卡片容器样式
const EventCard = styled('div', {
  padding: '1.5rem',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  marginBottom: '1.5rem',
  border: '1px solid $borderColor'
});

// 事件标题样式
const EventTitle = styled('h4', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 事件描述样式
const EventDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  marginBottom: '1rem',
  lineHeight: 1.6
});

// 事件信息样式
const EventInfo = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1rem',
  fontSize: '0.9rem',
  color: '$textTertiary'
});

// 事件信息项样式
const EventInfoItem = styled('span', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      },
      secondary: {
        background: 'transparent',
        color: '$accentPrimary',
        border: '1px solid $accentPrimary',
        '&:hover': {
          background: 'rgba(6, 214, 160, 0.05)'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 行动召唤区块样式
const CtaSection = styled('div', {
  padding: '2.5rem',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  textAlign: 'center',
  color: 'white'
});

// 行动召唤标题样式
const CtaTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 行动召唤描述样式
const CtaDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  maxWidth: '800px',
  margin: '0 auto 2rem'
});

/**
 * NFT挖矿收益页面组件
 * 介绍NFT挖矿的收益和奖励系统
 */
const NftFarmRewards: React.FC = () => {
  const { t } = useTranslation();
  
  // 奖励类型数据
  const rewardTypes = [
    {
      title: t('nftFarm.rewards.types.passive.title', '被动挖矿收益'),
      subtitle: t('nftFarm.rewards.types.passive.subtitle', '每日基础奖励'),
      description: t('nftFarm.rewards.types.passive.description', '持有并质押NFT矿工，自动获得每日挖矿收益，无需额外操作。收益金额取决于NFT的稀有度和属性。'),
      benefits: [
        t('nftFarm.rewards.types.passive.benefit1', '稳定的每日加密货币收益'),
        t('nftFarm.rewards.types.passive.benefit2', '自动计算和累积'),
        t('nftFarm.rewards.types.passive.benefit3', '随时可提取'),
        t('nftFarm.rewards.types.passive.benefit4', '长期质押额外加成')
      ]
    },
    {
      title: t('nftFarm.rewards.types.staking.title', '质押奖励'),
      subtitle: t('nftFarm.rewards.types.staking.subtitle', '长期持有加成'),
      description: t('nftFarm.rewards.types.staking.description', '长期质押NFT矿工可获得额外的时间加成，质押时间越长，额外奖励越多，鼓励长期持有。'),
      benefits: [
        t('nftFarm.rewards.types.staking.benefit1', '每30天增加5%收益加成'),
        t('nftFarm.rewards.types.staking.benefit2', '最高可达50%额外收益'),
        t('nftFarm.rewards.types.staking.benefit3', '加成永久生效'),
        t('nftFarm.rewards.types.staking.benefit4', '无锁定期限，随时可取回')
      ]
    },
    {
      title: t('nftFarm.rewards.types.special.title', '特殊事件奖励'),
      subtitle: t('nftFarm.rewards.types.special.subtitle', '限时活动收益'),
      description: t('nftFarm.rewards.types.special.description', '参与平台举办的特殊挖矿事件和活动，获取额外奖励和稀有NFT。这些活动通常有特定的参与条件和时间限制。'),
      benefits: [
        t('nftFarm.rewards.types.special.benefit1', '高额一次性奖励'),
        t('nftFarm.rewards.types.special.benefit2', '获得限量版NFT的机会'),
        t('nftFarm.rewards.types.special.benefit3', '专属挖矿活动体验'),
        t('nftFarm.rewards.types.special.benefit4', '社区互动与合作机会')
      ]
    }
  ];
  
  // 收益层级数据
  const rewardTiers = [
    {
      tier: t('nftFarm.rewards.tiers.common', '普通'),
      baseReward: '0.001 BTC/日',
      stakingBonus: '最高+30%',
      specialEvents: t('nftFarm.rewards.tiers.basic', '基础参与'),
      upgradability: t('nftFarm.rewards.tiers.limited', '有限')
    },
    {
      tier: t('nftFarm.rewards.tiers.uncommon', '不普通'),
      baseReward: '0.002 BTC/日',
      stakingBonus: '最高+35%',
      specialEvents: t('nftFarm.rewards.tiers.standard', '标准参与'),
      upgradability: t('nftFarm.rewards.tiers.standard', '标准')
    },
    {
      tier: t('nftFarm.rewards.tiers.rare', '稀有'),
      baseReward: '0.004 BTC/日',
      stakingBonus: '最高+40%',
      specialEvents: t('nftFarm.rewards.tiers.priority', '优先参与'),
      upgradability: t('nftFarm.rewards.tiers.good', '良好')
    },
    {
      tier: t('nftFarm.rewards.tiers.epic', '史诗'),
      baseReward: '0.008 BTC/日',
      stakingBonus: '最高+45%',
      specialEvents: t('nftFarm.rewards.tiers.premium', '高级参与'),
      upgradability: t('nftFarm.rewards.tiers.excellent', '极佳')
    },
    {
      tier: t('nftFarm.rewards.tiers.legendary', '传说'),
      baseReward: '0.015 BTC/日',
      stakingBonus: '最高+50%',
      specialEvents: t('nftFarm.rewards.tiers.exclusive', '专属参与'),
      upgradability: t('nftFarm.rewards.tiers.unlimited', '无限')
    }
  ];
  
  // 特殊事件数据
  const specialEvents = [
    {
      title: t('nftFarm.rewards.events.goldenHour.title', '黄金挖矿时刻'),
      description: t('nftFarm.rewards.events.goldenHour.description', '每周特定时段，所有NFT挖矿收益翻倍。参与这一活动可以在短时间内获得大量额外收益。'),
      date: t('nftFarm.rewards.events.goldenHour.date', '每周六 20:00-22:00'),
      reward: t('nftFarm.rewards.events.goldenHour.reward', '挖矿收益 x2')
    },
    {
      title: t('nftFarm.rewards.events.treasureHunt.title', '宝藏猎人挑战'),
      description: t('nftFarm.rewards.events.treasureHunt.description', '限时寻宝活动，参与者需要完成特定任务来发现虚拟宝藏。成功完成挑战可获得稀有NFT和额外奖励。'),
      date: t('nftFarm.rewards.events.treasureHunt.date', '每月15日 - 20日'),
      reward: t('nftFarm.rewards.events.treasureHunt.reward', '稀有NFT + 0.05 BTC')
    },
    {
      title: t('nftFarm.rewards.events.minersBattle.title', '矿工大对决'),
      description: t('nftFarm.rewards.events.minersBattle.description', '玩家可将自己的NFT矿工派遣参加虚拟挖矿比赛，表现最佳的矿工将获得丰厚奖励和排名提升。'),
      date: t('nftFarm.rewards.events.minersBattle.date', '每季度首月'),
      reward: t('nftFarm.rewards.events.minersBattle.reward', '季度奖池分配 + 稀有道具')
    }
  ];
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('nftFarm.rewards.title', 'NFT挖矿收益')}</Title>
        <Subtitle>
          {t('nftFarm.rewards.subtitle', '了解我们多样化的NFT挖矿奖励体系，最大化您的数字资产收益')}
        </Subtitle>
      </TitleSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.rewards.typesTitle', '奖励类型')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.rewards.typesDescription', '我们的NFT挖矿平台提供多种类型的奖励机制，确保持有者可以通过不同方式获取收益。')}
        </SectionDescription>
        
        <RewardsGrid>
          {rewardTypes.map((reward, index) => (
            <RewardCard key={index}>
              <RewardCardHeader>
                <RewardCardTitle>{reward.title}</RewardCardTitle>
                <RewardCardSubtitle>{reward.subtitle}</RewardCardSubtitle>
              </RewardCardHeader>
              <RewardCardContent>
                <RewardCardDescription>{reward.description}</RewardCardDescription>
                <RewardList>
                  {reward.benefits.map((benefit, i) => (
                    <RewardListItem key={i}>
                      <RewardListItemIcon>✓</RewardListItemIcon>
                      {benefit}
                    </RewardListItem>
                  ))}
                </RewardList>
              </RewardCardContent>
            </RewardCard>
          ))}
        </RewardsGrid>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.rewards.tiersTitle', '收益层级')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.rewards.tiersDescription', '不同稀有度的NFT矿工提供不同级别的挖矿收益和特权。稀有度越高，收益潜力越大。')}
        </SectionDescription>
        
        <TierTableContainer>
          <TierTable>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('nftFarm.rewards.tierColumn', 'NFT稀有度')}</TableHeaderCell>
                <TableHeaderCell>{t('nftFarm.rewards.baseRewardColumn', '基础收益')}</TableHeaderCell>
                <TableHeaderCell>{t('nftFarm.rewards.stakingBonusColumn', '质押加成')}</TableHeaderCell>
                <TableHeaderCell>{t('nftFarm.rewards.specialEventsColumn', '特殊活动')}</TableHeaderCell>
                <TableHeaderCell>{t('nftFarm.rewards.upgradabilityColumn', '升级潜力')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {rewardTiers.map((tier, index) => (
                <TableRow key={index}>
                  <SpecialTableCell>{tier.tier}</SpecialTableCell>
                  <TableCell>{tier.baseReward}</TableCell>
                  <TableCell>{tier.stakingBonus}</TableCell>
                  <TableCell>{tier.specialEvents}</TableCell>
                  <TableCell>{tier.upgradability}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </TierTable>
        </TierTableContainer>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.rewards.optimizationTitle', '收益优化')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.rewards.optimizationDescription', '通过合理的策略和组合，可以显著提高您的NFT挖矿收益。以下是一些优化收益的方法。')}
        </SectionDescription>
        
        <ImageTextBlock>
          <ImageContainer>
            <Image style={{ background: 'url("/assets/nft-rewards-optimization.jpg") center/cover no-repeat' }} />
          </ImageContainer>
          <TextContent>
            <TextTitle>{t('nftFarm.rewards.combinationTitle', 'NFT组合策略')}</TextTitle>
            <TextParagraph>
              {t('nftFarm.rewards.combinationDescription', '通过组合不同类型的NFT，可以创造协同效应，获得额外的收益加成。例如，将矿工NFT部署在合适的矿区土地上，并装备匹配的工具NFT，可以显著提高整体挖矿效率。')}
            </TextParagraph>
            <TextParagraph>
              {t('nftFarm.rewards.combinationTips', '推荐组合：史诗级矿工 + 稀有级土地 + 史诗级工具，可获得高达75%的总收益加成。')}
            </TextParagraph>
          </TextContent>
        </ImageTextBlock>
        
        <ImageTextBlock reverse>
          <ImageContainer>
            <Image style={{ background: 'url("/assets/nft-rewards-upgrades.jpg") center/cover no-repeat' }} />
          </ImageContainer>
          <TextContent>
            <TextTitle>{t('nftFarm.rewards.upgradeTitle', 'NFT升级与进化')}</TextTitle>
            <TextParagraph>
              {t('nftFarm.rewards.upgradeDescription', '随着时间推移，您可以使用挖矿收益购买升级材料，提升NFT的属性和挖矿效率。升级系统允许低稀有度的NFT逐步提升到更高层级，解锁更多收益潜力。')}
            </TextParagraph>
            <TextParagraph>
              {t('nftFarm.rewards.upgradeTips', '升级建议：集中资源优先升级高稀有度NFT，可获得最佳投资回报率；或者通过融合多个低级NFT创造更高级别的NFT。')}
            </TextParagraph>
          </TextContent>
        </ImageTextBlock>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>{t('nftFarm.rewards.eventsTitle', '特殊挖矿事件')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.rewards.eventsDescription', '我们定期举办特殊挖矿活动，为参与者提供额外的奖励机会。这些活动通常有时间限制，参与越早获益越大。')}
        </SectionDescription>
        
        <EventsContainer>
          {specialEvents.map((event, index) => (
            <EventCard key={index}>
              <EventTitle>{event.title}</EventTitle>
              <EventDescription>{event.description}</EventDescription>
              <EventInfo>
                <EventInfoItem>
                  <span>📅</span> {event.date}
                </EventInfoItem>
                <EventInfoItem>
                  <span>🏆</span> {event.reward}
                </EventInfoItem>
              </EventInfo>
            </EventCard>
          ))}
        </EventsContainer>
      </ContentSection>
      
      <CtaSection>
        <CtaTitle>{t('nftFarm.rewards.ctaTitle', '开始赚取您的挖矿收益')}</CtaTitle>
        <CtaDescription>
          {t('nftFarm.rewards.ctaDescription', '立即加入我们的NFT挖矿平台，探索丰富的奖励机制，获取被动加密货币收益。')}
        </CtaDescription>
        <Link to="/products/nft-farm/collections">
          <Button variant="primary">
            {t('nftFarm.rewards.ctaButton', '浏览NFT收藏品')}
          </Button>
        </Link>
      </CtaSection>
    </PageContainer>
  );
};

export default NftFarmRewards; 